import { useEffect, useState } from 'react';
import { listTemplates, listTemplateRows } from '../../lib/deliverables';
import type { DeliverableTemplate, DeliverableTemplateRow } from '../../types/deliverables';
import { Spinner } from '../ui/Spinner';
import { ClipboardCheck, FileSpreadsheet } from 'lucide-react';

/**
 * Vista de admin: lista plantillas + permite ver sus filas (read-only por
 * ahora). En una segunda iteración se agrega edición inline (add/remove/
 * reorder filas).
 *
 * Nota: la plantilla default ya viene poblada por el script de Python
 * `analisis/import_deliverables_template.py` con las 168 filas del Excel
 * histórico de CUBUS.
 */
export function AdminView() {
  const [templates, setTemplates] = useState<DeliverableTemplate[]>([]);
  const [selected, setSelected]   = useState<string | null>(null);
  const [rows, setRows]           = useState<DeliverableTemplateRow[]>([]);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const ts = await listTemplates();
      setTemplates(ts);
      if (ts.length > 0) setSelected(ts[0].id);
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (!selected) return;
    (async () => {
      const rs = await listTemplateRows(selected);
      setRows(rs);
    })();
  }, [selected]);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Spinner size="lg" /></div>;
  }

  const groupedBySheet = {
    modelado: rows.filter((r) => r.sheet === 'modelado'),
    laminas:  rows.filter((r) => r.sheet === 'laminas'),
  };

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8 overflow-y-auto">
      <header className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-cubus-lime/10 border border-cubus-lime/30 flex items-center justify-center">
          <ClipboardCheck className="w-6 h-6 text-cubus-lime" />
        </div>
        <div>
          <h1 className="text-display text-2xl lg:text-3xl font-bold text-white">Plantillas de Entregables</h1>
          <p className="text-muted text-sm">Definición maestra que se clona a cada alumno al iniciar su entregable.</p>
        </div>
      </header>

      {templates.length === 0 ? (
        <div className="text-center text-muted py-12">No hay plantillas creadas.</div>
      ) : (
        <>
          {/* Selector de plantilla */}
          <div className="flex flex-wrap gap-2">
            {templates.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setSelected(t.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all border ${
                  selected === t.id
                    ? 'bg-cubus-lime/15 text-cubus-lime border-cubus-lime/40'
                    : 'glass-deep text-white/70 hover:text-white border-white/[0.08]'
                }`}
              >
                <FileSpreadsheet className="w-4 h-4" />
                {t.name}
                {t.is_default && <span className="text-[10px] uppercase tracking-wider opacity-60">default</span>}
              </button>
            ))}
          </div>

          {/* Resumen */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Stat label="Total filas"          value={rows.length} />
            <Stat label="Hoja Modelado"        value={groupedBySheet.modelado.length} />
            <Stat label="Hoja Láminas"         value={groupedBySheet.laminas.length} />
          </div>

          {/* Listado de filas — solo lectura por ahora */}
          <div className="glass-surface rounded-xl border border-white/[0.06] overflow-hidden">
            <div className="px-5 py-3 bg-white/[0.02] border-b border-white/[0.06]">
              <h3 className="font-semibold text-white">Filas de la plantilla</h3>
              <p className="text-xs text-white/50">
                Edición inline llegará en la próxima iteración. Por ahora, las filas se gestionan
                desde el script <code className="px-1 py-0.5 rounded bg-white/[0.05] text-cubus-lime/80">analisis/import_deliverables_template.py</code>.
              </p>
            </div>
            <div className="max-h-[600px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-cubus-deep">
                  <tr className="text-[10px] uppercase tracking-wider text-white/40 border-b border-white/[0.06]">
                    <th className="text-left px-4 py-2">Hoja</th>
                    <th className="text-left px-4 py-2">Etapa</th>
                    <th className="text-left px-4 py-2">Sub-etapa</th>
                    <th className="text-left px-4 py-2">Actividad</th>
                    <th className="text-center px-2 py-2">LOD</th>
                    <th className="text-center px-2 py-2">E1</th>
                    <th className="text-center px-2 py-2">E2</th>
                    <th className="text-center px-2 py-2">E3</th>
                    <th className="text-center px-2 py-2">Nivel</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-t border-white/[0.04] hover:bg-white/[0.02]">
                      <td className="px-4 py-1.5 text-xs text-white/40 uppercase">{r.sheet}</td>
                      <td className="px-4 py-1.5 text-white/70">{r.section}</td>
                      <td className="px-4 py-1.5 text-white/60">{r.subsection}</td>
                      <td className="px-4 py-1.5 text-white/90">{r.activity}</td>
                      <td className="text-center text-white/60">{r.lod}</td>
                      <td className="text-center">{r.delivery_e1 ? '✓' : ''}</td>
                      <td className="text-center">{r.delivery_e2 ? '✓' : ''}</td>
                      <td className="text-center">{r.delivery_e3 ? '✓' : ''}</td>
                      <td className="text-center text-cubus-lime/70 text-xs">{r.is_leveled ? '🔢' : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="glass-surface rounded-xl border border-white/[0.06] p-4">
      <div className="text-xs uppercase tracking-[2px] text-white/40 font-semibold">{label}</div>
      <div className="text-2xl font-bold text-white mt-1">{value}</div>
    </div>
  );
}
